import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import {
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { payrollAPI } from '../../services/api'

interface Employee {
  id: number
  employee_id: string
  first_name: string
  last_name: string
  full_name: string
  department: string
  position: string
  current_compensation_structure?: {
    structure_type: string
    base_rate: number
    currency: string
  }
}

interface PayrollCalculationData {
  employee_id: number
  pay_period_year: number
  pay_period_month: number
  include_overtime: boolean
  overtime_threshold?: number
  additional_earnings: Array<{
    name: string
    amount: number
    description?: string
  }>
  additional_deductions: Array<{
    name: string
    amount: number
    description?: string
  }>
}

interface PayrollCalculationResult {
  employee_id: number
  pay_period_year: number
  pay_period_month: number
  calculation_summary: {
    base_pay: number
    overtime_pay: number
    bonus_amount: number
    total_deductions: number
    gross_pay: number
    net_pay: number
    compensation_structure_type: string
    base_rate: number
    hours_worked: number
    days_worked: number
    overtime_hours: number
  }
  payroll_items: Array<{
    payroll_type_id: number
    amount: number
    hours?: number
    rate?: number
    description: string
  }>
  warnings: string[]
}

interface PayrollCalculationDialogProps {
  open: boolean
  onClose: () => void
  onCalculate: (data: PayrollCalculationData) => Promise<PayrollCalculationResult>
  onCreateRecord?: (calculationResult: PayrollCalculationResult) => Promise<void>
  employees: Employee[]
  loading?: boolean
}

const schema = yup.object({
  employee_id: yup.number().required('Employee is required'),
  pay_period_year: yup.number().min(2020).max(2100).required('Year is required'),
  pay_period_month: yup.number().min(1).max(12).required('Month is required'),
  include_overtime: yup.boolean().required(),
  overtime_threshold: yup.number().positive().optional(),
})

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

export const PayrollCalculationDialog: React.FC<PayrollCalculationDialogProps> = ({
  open,
  onClose,
  onCalculate,
  onCreateRecord,
  employees,
  loading = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [calculationResult, setCalculationResult] = useState<PayrollCalculationResult | null>(null)
  const [calculating, setCalculating] = useState(false)
  const [creating, setCreating] = useState(false)
  const [additionalEarnings, setAdditionalEarnings] = useState<Array<{ name: string; amount: number; description?: string }>>([])
  const [additionalDeductions, setAdditionalDeductions] = useState<Array<{ name: string; amount: number; description?: string }>>([])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PayrollCalculationData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      employee_id: employees.length > 0 ? employees[0].id : 0,
      pay_period_year: new Date().getFullYear(),
      pay_period_month: new Date().getMonth() + 1,
      include_overtime: true,
      overtime_threshold: 40,
      additional_earnings: [],
      additional_deductions: [],
    },
  })

  const selectedEmployeeId = watch('employee_id')
  const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId)

  useEffect(() => {
    if (!open) {
      setCalculationResult(null)
      setAdditionalEarnings([])
      setAdditionalDeductions([])
      reset({
        employee_id: employees.length > 0 ? employees[0].id : 0,
        pay_period_year: new Date().getFullYear(),
        pay_period_month: new Date().getMonth() + 1,
        include_overtime: true,
        overtime_threshold: 40,
        additional_earnings: [],
        additional_deductions: [],
      })
    }
  }, [open, reset, employees])

  const handleCalculate = async (data: PayrollCalculationData) => {
    setCalculating(true)
    try {
      const result = await onCalculate({
        ...data,
        additional_earnings: additionalEarnings,
        additional_deductions: additionalDeductions,
      })
      setCalculationResult(result)
    } catch (error) {
      console.error('Calculation failed:', error)
    } finally {
      setCalculating(false)
    }
  }

  const handleCreateRecord = async () => {
    if (!calculationResult || !onCreateRecord) return
    
    setCreating(true)
    try {
      await onCreateRecord(calculationResult)
      onClose()
    } catch (error) {
      console.error('Failed to create payroll record:', error)
    } finally {
      setCreating(false)
    }
  }

  const addEarning = () => {
    setAdditionalEarnings([...additionalEarnings, { name: '', amount: 0, description: '' }])
  }

  const removeEarning = (index: number) => {
    setAdditionalEarnings(additionalEarnings.filter((_, i) => i !== index))
  }

  const updateEarning = (index: number, field: string, value: any) => {
    const updated = [...additionalEarnings]
    updated[index] = { ...updated[index], [field]: value }
    setAdditionalEarnings(updated)
  }

  const addDeduction = () => {
    setAdditionalDeductions([...additionalDeductions, { name: '', amount: 0, description: '' }])
  }

  const removeDeduction = (index: number) => {
    setAdditionalDeductions(additionalDeductions.filter((_, i) => i !== index))
  }

  const updateDeduction = (index: number, field: string, value: any) => {
    const updated = [...additionalDeductions]
    updated[index] = { ...updated[index], [field]: value }
    setAdditionalDeductions(updated)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
    }).format(amount)
  }

  const getCompensationTypeLabel = (type: string) => {
    switch (type) {
      case 'hourly_rate': return 'Hourly Rate'
      case 'daily_rate': return 'Daily Rate'
      case 'weekly_rate': return 'Weekly Rate'
      case 'monthly_salary': return 'Monthly Salary'
      default: return type
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '20px' },
          backgroundColor: '#f5f7fa',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} component="span" sx={{ fontWeight: 700 }}>
            Payroll Calculation
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            onClick={onClose}
            size="small"
            edge="end"
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{
        p: { xs: 2, sm: 3 },
        overflowY: 'auto',
        backgroundColor: '#f5f7fa',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
          '&:hover': {
            background: '#555',
          },
        },
      }}>
        <Grid container spacing={3}>
          {/* Employee Selection and Period */}
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: isMobile ? 2 : 3, borderRadius: 0 }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Employee & Period
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="employee_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.employee_id} size={isMobile ? 'small' : 'medium'}>
                          <InputLabel>Employee</InputLabel>
                          <Select {...field} label="Employee" disabled={loading || calculating}>
                            {employees.map((employee) => (
                              <MenuItem key={employee.id} value={employee.id}>
                                <Box>
                                  <Typography variant={isMobile ? 'body2' : 'body1'}>
                                    {employee.full_name} ({employee.employee_id})
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                                    {employee.department} - {employee.position}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="pay_period_year"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Year"
                          type="number"
                          fullWidth
                          size={isMobile ? 'small' : 'medium'}
                          error={!!errors.pay_period_year}
                          helperText={errors.pay_period_year?.message}
                          disabled={loading || calculating}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="pay_period_month"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.pay_period_month} size={isMobile ? 'small' : 'medium'}>
                          <InputLabel>Month</InputLabel>
                          <Select {...field} label="Month" disabled={loading || calculating}>
                            {MONTHS.map((month) => (
                              <MenuItem key={month.value} value={month.value}>
                                {month.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                {selectedEmployee && (
                  <Box sx={{ mt: 2, p: isMobile ? 1.5 : 2, backgroundColor: 'background.default', borderRadius: 0 }}>
                    <Typography variant={isMobile ? 'caption' : 'subtitle2'} gutterBottom sx={{ fontWeight: 600 }}>
                      Current Compensation Structure
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={getCompensationTypeLabel(selectedEmployee.current_compensation_structure?.structure_type || '')}
                        color="primary"
                        size="small"
                        sx={{ borderRadius: 0, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                      />
                      <Chip
                        label={formatCurrency(selectedEmployee.current_compensation_structure?.base_rate || 0)}
                        color="secondary"
                        size="small"
                        sx={{ borderRadius: 0, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Calculation Options */}
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: isMobile ? 2 : 3, borderRadius: 0 }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Calculation Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="include_overtime"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                          <InputLabel>Include Overtime</InputLabel>
                          <Select {...field} label="Include Overtime" disabled={loading || calculating}>
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="overtime_threshold"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Overtime Threshold (hours)"
                          type="number"
                          fullWidth
                          size={isMobile ? 'small' : 'medium'}
                          error={!!errors.overtime_threshold}
                          helperText={errors.overtime_threshold?.message || "Hours after which overtime applies"}
                          disabled={loading || calculating}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Earnings and Deductions */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Additional Earnings & Deductions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Additional Earnings</Typography>
                      <Button onClick={addEarning} size="small" variant="outlined">
                        Add Earning
                      </Button>
                    </Box>
                    {additionalEarnings.map((earning, index) => (
                      <Card key={index} sx={{ mb: 2, p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Name"
                              value={earning.name}
                              onChange={(e) => updateEarning(index, 'name', e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Amount"
                              type="number"
                              value={earning.amount}
                              onChange={(e) => updateEarning(index, 'amount', parseFloat(e.target.value) || 0)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              onClick={() => removeEarning(index)}
                              color="error"
                              size="small"
                              fullWidth
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Card>
                    ))}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Additional Deductions</Typography>
                      <Button onClick={addDeduction} size="small" variant="outlined">
                        Add Deduction
                      </Button>
                    </Box>
                    {additionalDeductions.map((deduction, index) => (
                      <Card key={index} sx={{ mb: 2, p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Name"
                              value={deduction.name}
                              onChange={(e) => updateDeduction(index, 'name', e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Amount"
                              type="number"
                              value={deduction.amount}
                              onChange={(e) => updateDeduction(index, 'amount', parseFloat(e.target.value) || 0)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              onClick={() => removeDeduction(index)}
                              color="error"
                              size="small"
                              fullWidth
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Card>
                    ))}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Calculation Results */}
          {calculationResult && (
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon />
                    Calculation Results
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                        <CardContent>
                          <Typography variant="h4" color="primary.main">
                            {formatCurrency(calculationResult.calculation_summary.net_pay)}
                          </Typography>
                          <Typography variant="subtitle1">Net Pay</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={8}>
                      <Card sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Summary</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2">Base Pay: {formatCurrency(calculationResult.calculation_summary.base_pay)}</Typography>
                              <Typography variant="body2">Overtime Pay: {formatCurrency(calculationResult.calculation_summary.overtime_pay)}</Typography>
                              <Typography variant="body2">Bonuses: {formatCurrency(calculationResult.calculation_summary.bonus_amount)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">Gross Pay: {formatCurrency(calculationResult.calculation_summary.gross_pay)}</Typography>
                              <Typography variant="body2">Deductions: {formatCurrency(calculationResult.calculation_summary.total_deductions)}</Typography>
                              <Typography variant="body2">Hours Worked: {calculationResult.calculation_summary.hours_worked}</Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {calculationResult.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Warnings:</Typography>
                      <List dense>
                        {calculationResult.warnings.map((warning, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText primary={warning} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{
        backgroundColor: 'white',
        borderTop: '2px solid #f0f0f0',
        p: { xs: 2, sm: 3 },
        gap: { xs: 1, sm: 2 },
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        flexDirection: { xs: 'column', sm: 'row' },
      }}>
        {!isMobile && (
          <Button
            onClick={onClose}
            disabled={calculating || creating}
            variant="outlined"
            sx={{
              borderRadius: '16px',
              borderColor: '#e0e0e0',
              borderWidth: '2px',
              color: 'text.primary',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
                borderWidth: '2px',
              },
            }}
          >
            Cancel
          </Button>
        )}
        
        <Button
          onClick={handleSubmit(handleCalculate)}
          variant="contained"
          disabled={calculating || creating || !selectedEmployeeId}
          startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
          fullWidth={isMobile}
          sx={{
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            fontWeight: 600,
            px: { xs: 3, sm: 4 },
            py: { xs: 1.25, sm: 1.5 },
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f8a7f 0%, #2fd66d 100%)',
              boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
            },
            '&:active': {
              boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)',
            },
            '&:disabled': {
              opacity: 0.6,
              background: '#e0e0e0',
              color: 'text.secondary',
              boxShadow: 'none',
            },
          }}
        >
          {calculating ? 'Calculating...' : 'Calculate Payroll'}
        </Button>

        {calculationResult && onCreateRecord && (
          <Button
            onClick={handleCreateRecord}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <MoneyIcon />}
            fullWidth={isMobile}
            sx={{
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: 'white',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.25, sm: 1.5 },
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
              },
              '&:active': {
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
              },
              '&:disabled': {
                opacity: 0.6,
                background: '#e0e0e0',
                color: 'text.secondary',
                boxShadow: 'none',
              },
            }}
          >
            {creating ? 'Creating...' : 'Create Payroll Record'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default PayrollCalculationDialog
